import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import EntityActivityTabs from '@/components/feature/EntityActivityTabs';
import ClientBalanceCards from '@/components/feature/ClientBalanceCards';
import StoreTransferHistory from '@/components/feature/StoreTransferHistory';
import CustomerContextPanel from '@/pages/dashboard/apps/customers-manager/detail/components/CustomerContextPanel';
import ClientZifekActions from '@/pages/dashboard/apps/customers-manager/detail/components/ClientZifekActions';
import ClientInvoiceLinkModal from '@/pages/dashboard/apps/customers-manager/detail/components/ClientInvoiceLinkModal';
import { getLoyaltyPoints } from '@/lib/loyalty';
import { getCommerceId } from '@/lib/ownership';
import { buildExcelWorkbook } from '@/lib/excelExport';

interface Commande {
  id: number;
  titre: string;
  nomclient: string;
  email: string;
  tel: string;
  adresse: string;
  details: string;
  detailssup: string;
  totalht: string;
  totalttc: string;
  sommepayee: string;
  monaie: string;
  methodepayment: string;
  date_time: string;
  statut_com_vendeur: string;
  quantite: number;
  notevendeur: string;
}

interface Transaction {
  id: number;
  montant: number;
  methode: string;
  statut: string;
  date_time: string;
}

interface SiteVisit {
  id: number;
  ip: string;
  date: string;
  source: string;
  boutique: string;
  page: string;
}

interface ProductVisit {
  id: number;
  id_produit: number;
  product_name: string;
  date: string;
  lien: string;
  ip: string;
}

interface CartItem {
  id: number;
  product_name: string;
  prix: string;
  product_quantity: string;
  date: string;
}

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  subject: string;
  message: string;
  created_at: string;
  statusdemande: number;
}

interface FideliteTransaction {
  id: number;
  type: string;
  points: number;
  description: string;
  created_at: string;
}

interface Creance {
  id: number;
  client_nom: string;
  montant: number;
  montant_recu: number;
  date_echeance: string;
  statut: string;
  created_at: string;
}

interface Facture {
  id: number;
  numero: string;
  objet: string;
  montant_ht: number;
  montant_ttc: number;
  statut: string;
  date_echeance: string;
  created_at: string;
}

interface Devis {
  id: number;
  numero: string;
  objet: string;
  montant_ht: number;
  montant_ttc: number;
  statut: string;
  created_at: string;
}

interface ClientInfo {
  name: string;
  email: string;
  phone: string;
  address: string;
}

interface DigitalDownload {
  order_id: number;
  product_name: string;
  file_name: string | null;
  file_url: string | null;
  date: string;
}

interface ProjetFinance {
  id: number;
  titre: string;
  description: string | null;
  status: number;
  budget: string | null;
  montantpaye: number | null;
  montantrestant: number | null;
  datedebut: string | null;
  datedefin: string | null;
  facture: number;
  factureCount: number;
}

type TabKey = 'context' | 'orders' | 'transactions' | 'visits' | 'cart' | 'contacts' | 'fidelite' | 'downloads' | 'projets' | 'finances' | 'activity' | 'notes';

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'context', label: 'Contexte client', icon: 'ri-links-line' },
  { key: 'orders', label: 'Commandes', icon: 'ri-file-list-3-line' },
  { key: 'transactions', label: 'Transactions', icon: 'ri-exchange-dollar-line' },
  { key: 'visits', label: 'Visites', icon: 'ri-eye-line' },
  { key: 'cart', label: 'Panier', icon: 'ri-shopping-cart-line' },
  { key: 'contacts', label: 'Messages', icon: 'ri-message-3-line' },
  { key: 'fidelite', label: 'Fidélité', icon: 'ri-star-line' },
  { key: 'downloads', label: 'Téléchargements', icon: 'ri-download-cloud-2-line' },
  { key: 'projets', label: 'Projets & finance', icon: 'ri-projector-line' },
  { key: 'finances', label: 'Finances', icon: 'ri-bank-line' },
  { key: 'activity', label: 'Timeline & Notes', icon: 'ri-history-line' },
  { key: 'notes', label: 'Notes commandes', icon: 'ri-sticky-note-line' },
];

function toNum(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export default function CustomerDetailPage({ initialTab = 'orders' }: { initialTab?: TabKey } = {}) {
  const params = useParams<{ customerId?: string; clientId?: string }>();
  const customerId = params.customerId || params.clientId || '';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [balanceKey, setBalanceKey] = useState(0);
  const [creatingExcel, setCreatingExcel] = useState(false);

  const [clientInfo, setClientInfo] = useState<ClientInfo>({ name: '', email: '', phone: '', address: '' });
  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [siteVisits, setSiteVisits] = useState<SiteVisit[]>([]);
  const [productVisits, setProductVisits] = useState<ProductVisit[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [fideliteTrans, setFideliteTrans] = useState<FideliteTransaction[]>([]);
  const [fidelitePoints, setFidelitePoints] = useState<number>(0);
  const [creances, setCreances] = useState<Creance[]>([]);
  const [factures, setFactures] = useState<Facture[]>([]);
  const [devis, setDevis] = useState<Devis[]>([]);
  const [digitalDownloads, setDigitalDownloads] = useState<DigitalDownload[]>([]);
  const [projets, setProjets] = useState<ProjetFinance[]>([]);

  const fetchAll = useCallback(async (silent = false) => {
    if (!user || !customerId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const commerceId = user.id;
      const clientInt = parseInt(customerId, 10);
      const hasIntId = !Number.isNaN(clientInt);

      // Client info depuis clientshop (scopé au commerçant)
      const { data: shopClient } = await supabase
        .from('clientshop')
        .select('*')
        .eq('idshop', commerceId)
        .eq('idclient', customerId)
        .maybeSingle();

      if (shopClient) {
        setClientInfo({
          name: shopClient.nomclient || '',
          email: shopClient.email || '',
          phone: shopClient.telephone || '',
          address: shopClient.adresse || '',
        });
      }

      // Client info enrichie depuis users (si id numérique)
      if (hasIntId) {
        const { data: userData } = await supabase
          .from('users')
          .select('id, name, email, telephone, adresse')
          .eq('id', clientInt)
          .maybeSingle();

        if (userData) {
          setClientInfo(prev => ({
            name: prev.name || userData.name || '',
            email: prev.email || userData.email || '',
            phone: prev.phone || userData.telephone || '',
            address: prev.address || userData.adresse || '',
          }));
        }
      }

      // Commandes du commerçant pour ce client
      const { data: commandesData } = await supabase
        .from('commande')
        .select('*')
        .eq('idvendeur', commerceId)
        .eq('user_id', customerId)
        .order('date_time', { ascending: false });

      if (commandesData && commandesData.length > 0) {
        setCommandes(commandesData as Commande[]);
        const first = commandesData[0];
        setClientInfo(prev => ({
          name: prev.name || first.nomclient || '',
          email: prev.email || first.email || '',
          phone: prev.phone || first.tel || '',
          address: prev.address || first.adresse || '',
        }));
      }

      // Transactions (paiements) du commerçant pour ce client
      if (hasIntId) {
        const { data: transData } = await supabase
          .from('transaction')
          .select('*')
          .eq('idcommerce', commerceId)
          .eq('idclient', clientInt)
          .order('date_time', { ascending: false });
        setTransactions((transData || []) as Transaction[]);
      }

      // Visites site
      const { data: siteVisitsData } = await supabase
        .from('visitesiteweb')
        .select('*')
        .eq('user_id', customerId)
        .order('date', { ascending: false })
        .limit(100);
      setSiteVisits((siteVisitsData || []) as SiteVisit[]);

      // Visites produits
      const { data: prodVisitsData } = await supabase
        .from('visiteproduitsiteweb')
        .select('*')
        .eq('user_id', customerId)
        .order('date', { ascending: false })
        .limit(100);
      setProductVisits((prodVisitsData || []) as ProductVisit[]);

      // Panier
      const { data: cartData } = await supabase
        .from('panier')
        .select('*')
        .eq('idclient', customerId)
        .order('date', { ascending: false });
      setCartItems((cartData || []) as CartItem[]);

      // Messages de contact
      const { data: contactData } = await supabase
        .from('contact_messages')
        .select('*')
        .eq('user_id', customerId)
        .order('created_at', { ascending: false });
      setContactMessages((contactData || []) as ContactMessage[]);

      // Téléchargements digitaux achetés par ce client
      const { data: orderHeaders } = await supabase
        .from('order_headers')
        .select('id, created_at')
        .eq('customer_id', customerId);
      if (orderHeaders && orderHeaders.length > 0) {
        const orderIds = orderHeaders.map((o) => o.id);
        const orderDateMap = new Map<number, string>();
        orderHeaders.forEach((o) => orderDateMap.set(o.id, o.created_at));

        const { data: orderItems } = await supabase
          .from('order_items')
          .select('order_id, product_id, product_name')
          .in('order_id', orderIds);
        const items = orderItems || [];
        const productIds = [...new Set(items.map((it) => parseInt(it.product_id, 10)).filter((n) => !Number.isNaN(n)))];

        const digitalMap = new Map<number, { name: string; url: string | null; file: string | null }>();
        if (productIds.length > 0) {
          const { data: prods } = await supabase
            .from('product_items')
            .select('id, name, product_type, digital_file_url, digital_file_name')
            .in('id', productIds)
            .eq('product_type', 'digital');
          (prods || []).forEach((p) => digitalMap.set(p.id, { name: p.name, url: p.digital_file_url, file: p.digital_file_name }));
        }

        const downloads: DigitalDownload[] = [];
        for (const it of items) {
          const prod = digitalMap.get(parseInt(it.product_id, 10));
          if (!prod) continue;
          downloads.push({
            order_id: it.order_id,
            product_name: prod.name || it.product_name,
            file_name: prod.file,
            file_url: prod.url,
            date: orderDateMap.get(it.order_id) || '',
          });
        }
        downloads.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setDigitalDownloads(downloads);
      }

      // Fidélité (solde calculé depuis le registre pointtransactions)
      if (hasIntId) {
        const pts = await getLoyaltyPoints(commerceId, clientInt);
        setFidelitePoints(pts);

        const { data: fideliteTransData } = await supabase
          .from('pointtransactions')
          .select('*')
          .eq('user_id', clientInt)
          .eq('idcommerce', commerceId)
          .order('created_at', { ascending: false })
          .limit(100);
        setFideliteTrans((fideliteTransData || []) as FideliteTransaction[]);

        // Créances
        const { data: creancesData } = await supabase
          .from('creances')
          .select('*')
          .eq('client_id', clientInt)
          .eq('idcommerce', commerceId)
          .order('created_at', { ascending: false });
        setCreances((creancesData || []) as Creance[]);

        // Factures
        const { data: facturesData } = await supabase
          .from('factures')
          .select('*')
          .eq('client_id', clientInt)
          .eq('idcommerce', commerceId)
          .order('created_at', { ascending: false });
        setFactures((facturesData || []) as Facture[]);

        // Devis
        const { data: devisData } = await supabase
          .from('devis')
          .select('*')
          .eq('client_id', clientInt)
          .eq('idcommerce', commerceId)
          .order('created_at', { ascending: false });
        setDevis((devisData || []) as Devis[]);

        // Projets GestionPro du client + leur finance (budget / facturé / encaissé)
        // `projet.clientid` référence la fiche `clientshop`, tandis que la route
        // porte l'identifiant du compte client : on interroge les deux.
        const projetClientIds = [clientInt];
        if (shopClient?.id && !projetClientIds.includes(Number(shopClient.id))) {
          projetClientIds.push(Number(shopClient.id));
        }
        const { data: projetsData } = await supabase
          .from('projet')
          .select('id, titre, description, status, budget, montantpaye, montantrestant, datedebut, datedefin')
          .in('clientid', projetClientIds)
          .eq('owner', getCommerceId(user))
          .order('datedebut', { ascending: false });
        const projetRows = (projetsData || []) as Omit<ProjetFinance, 'facture' | 'factureCount'>[];
        if (projetRows.length > 0) {
          const ids = projetRows.map((p) => p.id);
          const { data: invData } = await supabase
            .from('finance_invoices')
            .select('project_id, total, amount_paid, doc_type, status')
            .in('project_id', ids);
          const byProject = new Map<number, { total: number; count: number }>();
          (invData || []).forEach((inv) => {
            const pid = Number(inv.project_id);
            if (!pid || inv.doc_type === 'quote' || inv.status === 'cancelled') return;
            const prev = byProject.get(pid) || { total: 0, count: 0 };
            byProject.set(pid, { total: prev.total + toNum(inv.total), count: prev.count + 1 });
          });
          setProjets(projetRows.map((p) => ({
            ...p,
            facture: byProject.get(p.id)?.total || 0,
            factureCount: byProject.get(p.id)?.count || 0,
          })));
        } else {
          setProjets([]);
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user, customerId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const formatDate = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const commandeStatusBadge = (s: string) => {
    const val = (s || '').toLowerCase();
    if (val.includes('annul') || val.includes('refus')) return 'bg-red-100 text-red-700';
    if (val.includes('livr') || val.includes('termin') || val.includes('pay') || val.includes('accept') || val.includes('confir')) return 'bg-green-100 text-green-800';
    if (val.includes('attente') || val.includes('encours') || val.includes('en cours')) return 'bg-accent-100 text-accent-800';
    return 'bg-background-100 text-foreground-600';
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center py-32">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  const waNumber = clientInfo.phone ? clientInfo.phone.replace(/[^0-9+]/g, '') : '';
  const hasContact = clientInfo.email || clientInfo.phone;
  const clientUserIdNum = parseInt(customerId, 10);
  const clientUserId = Number.isNaN(clientUserIdNum) ? null : clientUserIdNum;
  const totalSpent = commandes.reduce((s, o) => s + (toNum(o.sommepayee) || toNum(o.totalttc)), 0)
    + transactions.reduce((s, t) => s + toNum(t.montant), 0);

  // Exporte tout le dossier du client dans un classeur Excel multi-feuilles.
  const handleOpenInExcel = async () => {
    if (!user) return;
    setCreatingExcel(true);
    try {
      const fmtDay = (d: string) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');
      const sheets = [
        {
          name: 'Fiche client',
          headers: ['Champ', 'Valeur'],
          rows: [
            ['ID', customerId],
            ['Nom', clientInfo.name],
            ['Email', clientInfo.email],
            ['Téléphone', clientInfo.phone],
            ['Adresse', clientInfo.address],
            ['Points fidélité', String(fidelitePoints)],
            ['Total dépensé', String(totalSpent)],
          ],
        },
        {
          name: 'Commandes',
          headers: ['N°', 'Date', 'Détail', 'Statut', 'Total', 'Payé', 'Paiement'],
          rows: commandes.map((o) => [
            String(o.id),
            formatDate(o.date_time),
            o.titre || o.details || '',
            o.statut_com_vendeur || '',
            String(toNum(o.totalttc || o.sommepayee)),
            String(toNum(o.sommepayee)),
            o.methodepayment || '',
          ]),
        },
        {
          name: 'Transactions',
          headers: ['N°', 'Date', 'Montant', 'Méthode', 'Statut'],
          rows: transactions.map((t) => [String(t.id), formatDate(t.date_time), String(toNum(t.montant)), t.methode || '', t.statut || '']),
        },
        {
          name: 'Visites site',
          headers: ['Date', 'IP', 'Page', 'Source'],
          rows: siteVisits.map((v) => [formatDate(v.date), v.ip || '', v.page || '', v.source || '']),
        },
        {
          name: 'Visites produits',
          headers: ['Date', 'Produit', 'Lien', 'IP'],
          rows: productVisits.map((v) => [formatDate(v.date), `#${v.id_produit}`, v.lien || '', v.ip || '']),
        },
        {
          name: 'Panier',
          headers: ['Date', 'Produit', 'Qté', 'Prix'],
          rows: cartItems.map((it) => [formatDate(it.date), it.product_name || '', it.product_quantity || '1', it.prix || '0']),
        },
        {
          name: 'Messages',
          headers: ['Date', 'Nom', 'Email', 'Objet', 'Message', 'Statut'],
          rows: contactMessages.map((m) => [
            formatDate(m.created_at),
            m.name || '',
            m.email || '',
            m.subject || '',
            m.message || '',
            m.statusdemande === 1 ? 'Traité' : 'En attente',
          ]),
        },
        {
          name: 'Fidélité',
          headers: ['Date', 'Type', 'Points', 'Description'],
          rows: fideliteTrans.map((t) => [formatDate(t.created_at), t.type === 'gain' ? 'Gagné' : 'Utilisé', String(t.points), t.description || '']),
        },
        {
          name: 'Téléchargements',
          headers: ['Date', 'Produit', 'Fichier', 'Commande'],
          rows: digitalDownloads.map((d) => [formatDate(d.date), d.product_name || '', d.file_name || '', `#${d.order_id}`]),
        },
        {
          name: 'Factures',
          headers: ['N°', 'Objet', 'HT', 'TTC', 'Statut', 'Échéance'],
          rows: factures.map((f) => [f.numero || '', f.objet || '', String(toNum(f.montant_ht)), String(toNum(f.montant_ttc)), f.statut || '', fmtDay(f.date_echeance)]),
        },
        {
          name: 'Devis',
          headers: ['N°', 'Objet', 'HT', 'TTC', 'Statut', 'Date'],
          rows: devis.map((d) => [d.numero || '', d.objet || '', String(toNum(d.montant_ht)), String(toNum(d.montant_ttc)), d.statut || '', formatDate(d.created_at)]),
        },
        {
          name: 'Créances',
          headers: ['Client', 'Montant', 'Reçu', 'Reste', 'Échéance', 'Statut'],
          rows: creances.map((c) => [c.client_nom || '', String(toNum(c.montant)), String(toNum(c.montant_recu)), String(toNum(c.montant - c.montant_recu)), fmtDay(c.date_echeance), c.statut || '']),
        },
      ];
      await buildExcelWorkbook(
        user.id,
        `Client ${clientInfo.name || customerId} ${new Date().toISOString().slice(0, 10)}`,
        sheets,
      );
      navigate('/dashboard/file-manager');
    } catch {
      /* erreur déjà gérée par le module documents */
    } finally {
      setCreatingExcel(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* Back + Header */}
      <button onClick={() => navigate('/dashboard/customers-manager')} className="flex items-center gap-1 text-sm text-foreground-500 hover:text-foreground-800 mb-3 cursor-pointer">
        <i className="ri-arrow-left-line"></i>
        <span>Retour à la liste</span>
      </button>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center">
              <span className="text-xl font-bold text-accent-700">{clientInfo.name ? clientInfo.name.charAt(0).toUpperCase() : '?'}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold font-heading text-foreground-950">{clientInfo.name || `Client #${customerId}`}</h2>
              <p className="text-sm text-foreground-500">ID: {customerId}</p>
              <div className="flex flex-wrap gap-3 mt-2">
                {clientInfo.email && (
                  <span className="flex items-center gap-1.5 text-xs text-foreground-600">
                    <i className="ri-mail-line text-primary-500"></i>
                    {clientInfo.email}
                  </span>
                )}
                {clientInfo.phone && (
                  <span className="flex items-center gap-1.5 text-xs text-foreground-600">
                    <i className="ri-phone-line text-primary-500"></i>
                    {clientInfo.phone}
                  </span>
                )}
                {clientInfo.address && (
                  <span className="flex items-center gap-1.5 text-xs text-foreground-600">
                    <i className="ri-map-pin-line text-primary-500"></i>
                    {clientInfo.address}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-4 text-center items-start flex-wrap">
            <div className="bg-background-100 rounded-lg px-4 py-2">
              <p className="text-xs text-foreground-500">Commandes</p>
              <p className="text-lg font-bold text-foreground-950">{commandes.length}</p>
            </div>
            <div className="bg-background-100 rounded-lg px-4 py-2">
              <p className="text-xs text-foreground-500">Transactions</p>
              <p className="text-lg font-bold text-foreground-950">{transactions.length}</p>
            </div>
            <div className="bg-background-100 rounded-lg px-4 py-2">
              <p className="text-xs text-foreground-500">Total dépensé</p>
              <p className="text-lg font-bold text-primary-600">{totalSpent.toLocaleString()} MAD</p>
            </div>
            <div className="bg-background-100 rounded-lg px-4 py-2">
              <p className="text-xs text-foreground-500">Points fidélité</p>
              <p className="text-lg font-bold text-accent-600">{fidelitePoints}</p>
            </div>
            <div className="bg-background-100 rounded-lg px-4 py-2">
              <p className="text-xs text-foreground-500">Téléchargements</p>
              <p className="text-lg font-bold text-secondary-700">{digitalDownloads.length}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate(`/dashboard/orders/new?customerId=${customerId}`)}
                className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-circle-line"></i>
                <span>Créer une commande</span>
              </button>
              <button
                onClick={handleOpenInExcel}
                disabled={creatingExcel}
                className="flex items-center gap-2 px-4 py-2.5 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-100 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                title="Exporter tout le dossier du client (plusieurs feuilles) dans l'application Documents"
              >
                {creatingExcel ? (
                  <><i className="ri-loader-4-line animate-spin"></i><span>Création...</span></>
                ) : (
                  <><i className="ri-file-excel-2-line text-green-600"></i><span>Ouvrir dans Excel</span></>
                )}
              </button>
              {hasContact && (
                <div className="relative">
                  <button
                    onClick={() => setContactOpen(!contactOpen)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-message-2-line"></i>
                    <span>Contacter</span>
                    <i className={`${contactOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
                  </button>
                  {contactOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setContactOpen(false)}></div>
                      <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-20 py-1">
                        {clientInfo.phone && (
                          <a
                            href={`https://wa.me/${waNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                            onClick={() => setContactOpen(false)}
                          >
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                              <i className="ri-whatsapp-fill text-green-600"></i>
                            </div>
                            <div>
                              <p className="font-medium">WhatsApp</p>
                              <p className="text-xs text-foreground-500">{clientInfo.phone}</p>
                            </div>
                          </a>
                        )}
                        {clientInfo.email && (
                          <a
                            href={`mailto:${clientInfo.email}`}
                            className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                            onClick={() => setContactOpen(false)}
                          >
                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <i className="ri-mail-fill text-primary-600"></i>
                            </div>
                            <div>
                              <p className="font-medium">Email</p>
                              <p className="text-xs text-foreground-500">{clientInfo.email}</p>
                            </div>
                          </a>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Soldes Zifek — deux cartes : solde magasin + portefeuille */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-wallet-3-line"></i>
          </span>
          <div>
            <h3 className="text-sm font-bold text-foreground-950">Solde du client</h3>
            <p className="text-xs text-foreground-500">Solde magasin utilisable uniquement chez votre commerce.</p>
          </div>
        </div>
        <ClientBalanceCards
          merchantId={user?.id ?? null}
          clientUserId={clientUserId}
          currency={user?.monaie || 'MAD'}
          refreshKey={balanceKey}
          showWallet={false}
        />
      </div>

      {/* Actions Zifek — mises en avant juste après un scan de QR */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-flashlight-line"></i>
          </span>
          <div>
            <h3 className="text-sm font-bold text-foreground-950">Actions Zifek</h3>
            <p className="text-xs text-foreground-500">Crédit, solde magasin, portefeuille Zifek, transfert entre clients et fidélité.</p>
          </div>
        </div>
        <ClientZifekActions
          clientUserId={clientUserId}
          clientName={clientInfo.name}
          onDone={() => { setBalanceKey((k) => k + 1); fetchAll(true); }}
        />
      </div>

      {/* Historique dédié des transferts de solde magasin */}
      <div className="mb-6">
        <StoreTransferHistory
          merchantId={user?.id ?? null}
          clientUserId={clientUserId}
          currency={user?.monaie || 'MAD'}
          refreshKey={balanceKey}
        />
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchAll} className="ml-auto text-red-600 underline cursor-pointer">Réessayer</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-5 border-b border-background-200/70 pb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
              activeTab === tab.key
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/70'
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        {activeTab === 'context' && (
          <CustomerContextPanel
            commerceId={user?.id}
            customerId={customerId}
            clientName={clientInfo.name}
            clientEmail={clientInfo.email}
            clientPhone={clientInfo.phone}
          />
        )}
        {activeTab === 'orders' && (
          <OrdersTab commandes={commandes} formatDate={formatDate} commandeStatusBadge={commandeStatusBadge} />
        )}
        {activeTab === 'transactions' && (
          <TransactionsTab transactions={transactions} formatDate={formatDate} />
        )}
        {activeTab === 'visits' && (
          <VisitsTab siteVisits={siteVisits} productVisits={productVisits} formatDate={formatDate} />
        )}
        {activeTab === 'cart' && (
          <CartTab cartItems={cartItems} formatDate={formatDate} />
        )}
        {activeTab === 'contacts' && (
          <ContactsTab messages={contactMessages} formatDate={formatDate} />
        )}
        {activeTab === 'fidelite' && (
          <FideliteTab points={fidelitePoints} transactions={fideliteTrans} formatDate={formatDate} />
        )}
        {activeTab === 'downloads' && (
          <DownloadsTab downloads={digitalDownloads} formatDate={formatDate} />
        )}
        {activeTab === 'projets' && (
          <ProjetsFinanceTab
            projets={projets}
            formatDate={formatDate}
            onOpen={(id) => navigate(`/dashboard/gestionpro/${id}`)}
            commerceId={getCommerceId(user)}
            client={{ name: clientInfo.name, email: clientInfo.email, phone: clientInfo.phone }}
            onChanged={() => { void fetchAll(true); }}
          />
        )}
        {activeTab === 'finances' && (
          <FinancesTab creances={creances} factures={factures} devis={devis} formatDate={formatDate} />
        )}
        {activeTab === 'activity' && (
          <div className="p-4">
            <EntityActivityTabs
              commerceId={user?.id}
              entityType="customer"
              entityId={customerId}
              actorName={user?.name}
              actorUserId={user?.id}
            />
          </div>
        )}
        {activeTab === 'notes' && (
          <NotesTab commandes={commandes} onNoteSaved={fetchAll} />
        )}
      </div>
    </div>
  );
}

/* ──────────────── Orders Tab ──────────────── */
function OrdersTab({ commandes, formatDate, commandeStatusBadge }: { commandes: Commande[]; formatDate: (d: string) => string; commandeStatusBadge: (s: string) => string }) {
  if (commandes.length === 0) {
    return <div className="py-14 text-center text-foreground-500 text-sm">Aucune commande trouvée pour ce client</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-background-100 text-xs font-semibold text-foreground-500 uppercase">
            <th className="text-left px-4 py-3">N°</th>
            <th className="text-left px-4 py-3">Date</th>
            <th className="text-left px-4 py-3">Détail</th>
            <th className="text-left px-4 py-3">Statut</th>
            <th className="text-right px-4 py-3">Total</th>
            <th className="text-right px-4 py-3 hidden md:table-cell">Payé</th>
            <th className="text-left px-4 py-3 hidden md:table-cell">Paiement</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-background-200/70">
          {commandes.map((o) => (
            <tr key={o.id} className="hover:bg-background-50/50">
              <td className="px-4 py-3 font-mono text-xs text-foreground-600">#{o.id}</td>
              <td className="px-4 py-3 text-foreground-600 text-xs whitespace-nowrap">{formatDate(o.date_time)}</td>
              <td className="px-4 py-3">
                <span className="text-foreground-800 text-xs font-medium">{o.titre || o.details || '-'}</span>
                {o.quantite > 1 && <span className="text-xs text-foreground-400 ml-1">×{o.quantite}</span>}
              </td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${commandeStatusBadge(o.statut_com_vendeur)}`}>
                  {o.statut_com_vendeur || 'En attente'}
                </span>
              </td>
              <td className="px-4 py-3 text-right font-semibold text-foreground-900 whitespace-nowrap">
                {toNum(o.totalttc || o.sommepayee).toLocaleString()} {o.monaie || 'MAD'}
              </td>
              <td className="px-4 py-3 text-right text-foreground-600 hidden md:table-cell whitespace-nowrap">
                {toNum(o.sommepayee).toLocaleString()} {o.monaie || 'MAD'}
              </td>
              <td className="px-4 py-3 text-left text-xs text-foreground-500 hidden md:table-cell">{o.methodepayment || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ──────────────── Transactions Tab ──────────────── */
function TransactionsTab({ transactions, formatDate }: { transactions: Transaction[]; formatDate: (d: string) => string }) {
  if (transactions.length === 0) {
    return <div className="py-14 text-center text-foreground-500 text-sm">Aucune transaction trouvée pour ce client</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-background-100 text-xs font-semibold text-foreground-500 uppercase">
            <th className="text-left px-4 py-3">N°</th>
            <th className="text-left px-4 py-3">Date</th>
            <th className="text-right px-4 py-3">Montant</th>
            <th className="text-left px-4 py-3">Méthode</th>
            <th className="text-left px-4 py-3">Statut</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-background-200/70">
          {transactions.map((t) => (
            <tr key={t.id} className="hover:bg-background-50/50">
              <td className="px-4 py-3 font-mono text-xs text-foreground-600">#{t.id}</td>
              <td className="px-4 py-3 text-foreground-600 text-xs whitespace-nowrap">{formatDate(t.date_time)}</td>
              <td className="px-4 py-3 text-right font-semibold text-primary-600 whitespace-nowrap">{toNum(t.montant).toLocaleString()} MAD</td>
              <td className="px-4 py-3 text-foreground-700 text-xs">{t.methode || '-'}</td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                  (t.statut || '').toLowerCase().includes('annul') || (t.statut || '').toLowerCase().includes('echou')
                    ? 'bg-red-100 text-red-700'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {t.statut || 'Payée'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ──────────────── Visits Tab ──────────────── */
function VisitsTab({ siteVisits, productVisits, formatDate }: { siteVisits: SiteVisit[]; productVisits: ProductVisit[]; formatDate: (d: string) => string }) {
  return (
    <div className="divide-y divide-background-200/70">
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-globe-line text-primary-500"></i>
          Visites du site ({siteVisits.length})
        </h3>
        {siteVisits.length === 0 ? (
          <p className="text-xs text-foreground-500">Aucune visite enregistrée</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-background-100 text-foreground-500">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">IP</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">Page</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {siteVisits.map((v) => (
                  <tr key={v.id} className="hover:bg-background-50/50">
                    <td className="px-3 py-2 text-foreground-600 whitespace-nowrap">{formatDate(v.date)}</td>
                    <td className="px-3 py-2 text-foreground-500 font-mono">{v.ip}</td>
                    <td className="px-3 py-2 text-foreground-600 hidden sm:table-cell">{v.page || '-'}</td>
                    <td className="px-3 py-2 text-foreground-500 hidden md:table-cell">{v.source || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-shopping-bag-3-line text-accent-500"></i>
          Visites produits ({productVisits.length})
        </h3>
        {productVisits.length === 0 ? (
          <p className="text-xs text-foreground-500">Aucune visite produit enregistrée</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-background-100 text-foreground-500">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Produit</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">Lien</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {productVisits.map((v) => (
                  <tr key={v.id} className="hover:bg-background-50/50">
                    <td className="px-3 py-2 text-foreground-600 whitespace-nowrap">{formatDate(v.date)}</td>
                    <td className="px-3 py-2 text-foreground-800">#{v.id_produit}</td>
                    <td className="px-3 py-2 text-foreground-500 hidden sm:table-cell truncate max-w-[200px]">{v.lien || '-'}</td>
                    <td className="px-3 py-2 text-foreground-500 font-mono hidden md:table-cell">{v.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────── Cart Tab ──────────────── */
function CartTab({ cartItems, formatDate }: { cartItems: CartItem[]; formatDate: (d: string) => string }) {
  if (cartItems.length === 0) {
    return <div className="py-14 text-center text-foreground-500 text-sm">Aucun article dans le panier</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-background-100 text-xs font-semibold text-foreground-500 uppercase">
            <th className="text-left px-4 py-3">Date</th>
            <th className="text-left px-4 py-3">Produit</th>
            <th className="text-center px-4 py-3">Qté</th>
            <th className="text-right px-4 py-3">Prix</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-background-200/70">
          {cartItems.map((item) => (
            <tr key={item.id} className="hover:bg-background-50/50">
              <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">{formatDate(item.date)}</td>
              <td className="px-4 py-3 text-foreground-800">{item.product_name || '-'}</td>
              <td className="px-4 py-3 text-center text-foreground-700">{item.product_quantity || '1'}</td>
              <td className="px-4 py-3 text-right font-semibold text-foreground-900">{item.prix || '0'} MAD</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ──────────────── Contacts Tab ──────────────── */
function ContactsTab({ messages, formatDate }: { messages: ContactMessage[]; formatDate: (d: string) => string }) {
  if (messages.length === 0) {
    return <div className="py-14 text-center text-foreground-500 text-sm">Aucun message ou contact</div>;
  }
  return (
    <div className="divide-y divide-background-200/70">
      {messages.map((m) => (
        <div key={m.id} className="p-4 hover:bg-background-50/50">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-sm font-semibold text-foreground-900">{m.subject || 'Sans objet'}</p>
              <p className="text-xs text-foreground-500">{m.name} · {m.email}</p>
            </div>
            <div className="flex items-center gap-2">
              {m.statusdemande === 1 ? (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Traité</span>
              ) : (
                <span className="text-xs bg-background-200/70 text-foreground-500 px-2 py-0.5 rounded-full">En attente</span>
              )}
              <span className="text-xs text-foreground-400 whitespace-nowrap">{formatDate(m.created_at)}</span>
            </div>
          </div>
          <p className="text-sm text-foreground-600 leading-relaxed">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

/* ──────────────── Fidélité Tab ──────────────── */
function FideliteTab({ points, transactions, formatDate }: { points: number; transactions: FideliteTransaction[]; formatDate: (d: string) => string }) {
  return (
    <div className="divide-y divide-background-200/70">
      <div className="p-4 flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center">
          <i className="ri-star-fill text-2xl text-accent-600"></i>
        </div>
        <div>
          <p className="text-sm text-foreground-500">Points de fidélité</p>
          <p className="text-2xl font-bold text-accent-600">{points}</p>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-800 mb-3">Historique des transactions ({transactions.length})</h3>
        {transactions.length === 0 ? (
          <p className="text-xs text-foreground-500">Aucune transaction de fidélité</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-background-100 text-foreground-500">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-center px-3 py-2">Points</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {transactions.map((t) => (
                  <tr key={t.id} className="hover:bg-background-50/50">
                    <td className="px-3 py-2 text-foreground-600 whitespace-nowrap">{formatDate(t.created_at)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${t.type === 'gain' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {t.type === 'gain' ? 'Gagné' : 'Utilisé'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center font-semibold text-foreground-800">{t.points}</td>
                    <td className="px-3 py-2 text-foreground-500 hidden sm:table-cell">{t.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────── Finances Tab ──────────────── */
function FinancesTab({ creances, factures, devis, formatDate }: { creances: Creance[]; factures: Facture[]; devis: Devis[]; formatDate: (d: string) => string }) {
  return (
    <div className="divide-y divide-background-200/70">
      {/* Factures */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-file-text-line text-primary-500"></i>
          Factures ({factures.length})
        </h3>
        {factures.length === 0 ? (
          <p className="text-xs text-foreground-500">Aucune facture</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-background-100 text-foreground-500">
                  <th className="text-left px-3 py-2">N°</th>
                  <th className="text-left px-3 py-2">Objet</th>
                  <th className="text-right px-3 py-2">HT</th>
                  <th className="text-right px-3 py-2 hidden sm:table-cell">TTC</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Échéance</th>
                  <th className="text-left px-3 py-2">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {factures.map((f) => (
                  <tr key={f.id} className="hover:bg-background-50/50">
                    <td className="px-3 py-2 font-mono text-foreground-600">{f.numero}</td>
                    <td className="px-3 py-2 text-foreground-800">{f.objet}</td>
                    <td className="px-3 py-2 text-right text-foreground-700">{toNum(f.montant_ht).toLocaleString()} MAD</td>
                    <td className="px-3 py-2 text-right font-semibold text-foreground-800 hidden sm:table-cell">{toNum(f.montant_ttc).toLocaleString()} MAD</td>
                    <td className="px-3 py-2 text-foreground-500 hidden md:table-cell">{f.date_echeance ? new Date(f.date_echeance).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${f.statut === 'payée' || f.statut === 'paye' ? 'bg-green-100 text-green-700' : 'bg-background-200/70 text-foreground-600'}`}>
                        {f.statut}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Devis */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-draft-line text-accent-500"></i>
          Devis ({devis.length})
        </h3>
        {devis.length === 0 ? (
          <p className="text-xs text-foreground-500">Aucun devis</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-background-100 text-foreground-500">
                  <th className="text-left px-3 py-2">N°</th>
                  <th className="text-left px-3 py-2">Objet</th>
                  <th className="text-right px-3 py-2">HT</th>
                  <th className="text-right px-3 py-2 hidden sm:table-cell">TTC</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {devis.map((d) => (
                  <tr key={d.id} className="hover:bg-background-50/50">
                    <td className="px-3 py-2 font-mono text-foreground-600">{d.numero}</td>
                    <td className="px-3 py-2 text-foreground-800">{d.objet}</td>
                    <td className="px-3 py-2 text-right text-foreground-700">{toNum(d.montant_ht).toLocaleString()} MAD</td>
                    <td className="px-3 py-2 text-right font-semibold text-foreground-800 hidden sm:table-cell">{toNum(d.montant_ttc).toLocaleString()} MAD</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${d.statut === 'accepté' || d.statut === 'accepte' ? 'bg-green-100 text-green-700' : d.statut === 'refusé' ? 'bg-red-100 text-red-700' : 'bg-background-200/70 text-foreground-600'}`}>
                        {d.statut}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-foreground-500 hidden md:table-cell">{formatDate(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Créances */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-money-dollar-circle-line text-secondary-500"></i>
          Créances ({creances.length})
        </h3>
        {creances.length === 0 ? (
          <p className="text-xs text-foreground-500">Aucune créance</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-background-100 text-foreground-500">
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-right px-3 py-2">Montant</th>
                  <th className="text-right px-3 py-2 hidden sm:table-cell">Reçu</th>
                  <th className="text-right px-3 py-2">Reste</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Échéance</th>
                  <th className="text-left px-3 py-2">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {creances.map((c) => (
                  <tr key={c.id} className="hover:bg-background-50/50">
                    <td className="px-3 py-2 text-foreground-800">{c.client_nom}</td>
                    <td className="px-3 py-2 text-right text-foreground-700">{toNum(c.montant).toLocaleString()} MAD</td>
                    <td className="px-3 py-2 text-right text-foreground-600 hidden sm:table-cell">{toNum(c.montant_recu).toLocaleString()} MAD</td>
                    <td className="px-3 py-2 text-right font-semibold text-red-600">{toNum(c.montant - c.montant_recu).toLocaleString()} MAD</td>
                    <td className="px-3 py-2 text-foreground-500 hidden md:table-cell">{c.date_echeance ? new Date(c.date_echeance).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.statut === 'payée' || c.statut === 'paye' ? 'bg-green-100 text-green-700' : 'bg-accent-100 text-accent-700'}`}>
                        {c.statut || 'En cours'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────── Downloads Tab ──────────────── */
function DownloadsTab({ downloads, formatDate }: { downloads: DigitalDownload[]; formatDate: (d: string) => string }) {
  if (downloads.length === 0) {
    return (
      <div className="py-14 text-center text-foreground-500 text-sm">
        <i className="ri-download-cloud-2-line text-3xl text-foreground-300 block mb-2"></i>
        Ce client n&apos;a acheté aucun fichier numérique pour le moment.
      </div>
    );
  }
  return (
    <div>
      <div className="px-4 pt-4 flex items-center gap-2 text-sm font-semibold text-foreground-800">
        <i className="ri-download-cloud-2-line text-secondary-600"></i>
        {downloads.length} fichier{downloads.length > 1 ? 's' : ''} numérique{downloads.length > 1 ? 's' : ''} acheté{downloads.length > 1 ? 's' : ''}
      </div>
      <div className="divide-y divide-background-200/70 mt-2">
        {downloads.map((d, i) => (
          <div key={`${d.order_id}-${i}`} className="flex items-center gap-3 px-4 py-3 hover:bg-background-50/50">
            <span className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-file-download-line"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground-900 truncate">{d.product_name}</p>
              <p className="text-xs text-foreground-500 truncate">
                {d.file_name || 'Fichier'} · Commande #{d.order_id}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-foreground-400 whitespace-nowrap hidden sm:block">{formatDate(d.date)}</span>
              {d.file_url ? (
                <a
                  href={d.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
                >
                  <i className="ri-download-line"></i>Télécharger
                </a>
              ) : (
                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-400 whitespace-nowrap">
                  Lien en préparation
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────────── Projets & Finance Tab ──────────────── */
const PROJET_STATUS: Record<number, { label: string; className: string }> = {
  1: { label: 'En cours', className: 'bg-accent-100 text-accent-700' },
  2: { label: 'Terminé', className: 'bg-secondary-100 text-secondary-700' },
  3: { label: 'Annulé', className: 'bg-background-200/70 text-foreground-500' },
  4: { label: 'En attente', className: 'bg-background-100 text-foreground-600' },
};

function ProjetsFinanceTab({
  projets,
  formatDate,
  onOpen,
  commerceId,
  client,
  onChanged,
}: {
  projets: ProjetFinance[];
  formatDate: (d: string) => string;
  onOpen: (id: number) => void;
  commerceId: number | null;
  client: { name?: string; email?: string; phone?: string };
  onChanged: () => void;
}) {
  const [linkTarget, setLinkTarget] = useState<ProjetFinance | null>(null);
  const totals = projets.reduce((acc, p) => {
    const budget = toNum(p.budget);
    const base = budget > 0 ? budget : p.facture;
    const paid = toNum(p.montantpaye);
    return {
      budget: acc.budget + base,
      facture: acc.facture + p.facture,
      paid: acc.paid + paid,
      restant: acc.restant + Math.max(0, base - paid),
    };
  }, { budget: 0, facture: 0, paid: 0, restant: 0 });

  if (projets.length === 0) {
    return (
      <div className="py-14 text-center px-4">
        <i className="ri-projector-line text-3xl text-foreground-300 block mb-2"></i>
        <p className="text-sm text-foreground-600">Aucun projet GestionPro lié à ce client.</p>
        <p className="text-xs text-foreground-400 mt-1">Créez un projet depuis GestionPro en sélectionnant ce client pour suivre son budget ici.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Synthèse */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-background-200/70">
        <div className="bg-background-100/70 rounded-lg p-3">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Budget total</p>
          <p className="text-base font-bold text-foreground-950">{totals.budget.toLocaleString('fr-FR')} MAD</p>
        </div>
        <div className="bg-background-100/70 rounded-lg p-3">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Facturé</p>
          <p className="text-base font-bold text-foreground-950">{totals.facture.toLocaleString('fr-FR')} MAD</p>
        </div>
        <div className="bg-background-100/70 rounded-lg p-3">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Encaissé</p>
          <p className="text-base font-bold text-primary-700">{totals.paid.toLocaleString('fr-FR')} MAD</p>
        </div>
        <div className="bg-background-100/70 rounded-lg p-3">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Reste à payer</p>
          <p className={`text-base font-bold ${totals.restant > 0 ? 'text-amber-700' : 'text-secondary-700'}`}>{totals.restant.toLocaleString('fr-FR')} MAD</p>
        </div>
      </div>

      {/* Projets */}
      <div className="divide-y divide-background-200/70">
        {projets.map((p) => {
          const budget = toNum(p.budget);
          const base = budget > 0 ? budget : p.facture;
          const paid = toNum(p.montantpaye);
          const rest = Math.max(0, base - paid);
          const ratio = base > 0 ? Math.min(100, Math.round((paid / base) * 100)) : 0;
          const meta = PROJET_STATUS[p.status] || PROJET_STATUS[1];
          return (
            <div key={p.id} className="p-4 hover:bg-background-50/60 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground-900">{p.titre}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${meta.className}`}>{meta.label}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">{p.factureCount} facture(s)</span>
                  </div>
                  {p.description && <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{p.description}</p>}
                  <p className="text-[11px] text-foreground-400 mt-1 flex items-center gap-1.5 flex-wrap">
                    <i className="ri-calendar-line"></i>
                    {p.datedebut ? formatDate(p.datedebut) : '—'}
                    <span className="text-foreground-300">→</span>
                    {p.datedefin ? formatDate(p.datedefin) : '—'}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-1.5 self-start flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setLinkTarget(p)}
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-link-m"></i>Relier une facture
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpen(p.id)}
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-external-link-line"></i>Ouvrir le projet
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-foreground-400">Budget</p>
                  <p className="text-xs font-semibold text-foreground-900">{base.toLocaleString('fr-FR')} MAD</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-foreground-400">Facturé</p>
                  <p className="text-xs font-semibold text-foreground-900">{p.facture.toLocaleString('fr-FR')} MAD</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-foreground-400">Payé</p>
                  <p className="text-xs font-semibold text-primary-700">{paid.toLocaleString('fr-FR')} MAD</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-foreground-400">Restant</p>
                  <p className={`text-xs font-semibold ${rest > 0 ? 'text-amber-700' : 'text-secondary-700'}`}>{rest.toLocaleString('fr-FR')} MAD</p>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-background-100 overflow-hidden">
                  <div className={`h-full rounded-full ${ratio >= 100 ? 'bg-secondary-500' : 'bg-accent-500'}`} style={{ width: `${ratio}%` }}></div>
                </div>
                <span className="text-[11px] font-medium text-foreground-600 w-10 text-right">{ratio}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {linkTarget && (
        <ClientInvoiceLinkModal
          commerceId={commerceId}
          projectId={linkTarget.id}
          projectTitle={linkTarget.titre}
          client={client}
          onClose={() => setLinkTarget(null)}
          onLinked={onChanged}
        />
      )}
    </div>
  );
}

/* ──────────────── Notes Tab ──────────────── */
function NotesTab({ commandes, onNoteSaved }: { commandes: Commande[]; onNoteSaved: () => void }) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  const commandesWithNotes = commandes.filter(o => o.notevendeur);

  const startEdit = (o: Commande) => {
    setEditingId(o.id);
    setEditText(o.notevendeur || '');
  };

  const saveNote = async (commandeId: number) => {
    setSaving(true);
    const { error } = await supabase
      .from('commande')
      .update({ notevendeur: editText })
      .eq('id', commandeId);
    if (!error) {
      setEditingId(null);
      onNoteSaved();
    }
    setSaving(false);
  };

  return (
    <div className="divide-y divide-background-200/70">
      {commandesWithNotes.length === 0 ? (
        <div className="py-14 text-center text-foreground-500 text-sm">
          <i className="ri-sticky-note-line text-3xl text-foreground-300 block mb-2"></i>
          Aucune note interne sur les commandes de ce client.
        </div>
      ) : (
        commandesWithNotes.map((o) => (
          <div key={o.id} className="p-4 hover:bg-background-50/50">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-foreground-500">Commande #{o.id}</span>
                <span className="text-xs text-foreground-400">
                  {new Date(o.date_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
              {editingId === o.id ? (
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditingId(null)} className="text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer">
                    Annuler
                  </button>
                  <button
                    onClick={() => saveNote(o.id)}
                    disabled={saving}
                    className="text-xs px-3 py-1 bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 cursor-pointer disabled:opacity-50"
                  >
                    {saving ? '...' : 'Enregistrer'}
                  </button>
                </div>
              ) : (
                <button onClick={() => startEdit(o)} className="text-xs text-foreground-400 hover:text-primary-500 cursor-pointer flex items-center gap-1">
                  <i className="ri-edit-line"></i>
                  Modifier
                </button>
              )}
            </div>
            {editingId === o.id ? (
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
                autoFocus
                maxLength={500}
              ></textarea>
            ) : (
              <p className="text-sm text-foreground-700 leading-relaxed bg-background-100 rounded-md p-3">{o.notevendeur}</p>
            )}
          </div>
        ))
      )}
    </div>
  );
}